// Copyright © 2026 Gian Marco Cappelli — SAPIENS (finsapiens.it). Tutti i diritti riservati. Sviluppato su commissione esclusiva. Vietata la riproduzione senza autorizzazione scritta.
import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { EducBox, ResultCard, fmt } from './shared';

// Tassi mora 2026 (aggiornati semestralmente)
const TASSI = {
  legale: 2.5,         // BCE 3% - 0.5% circa, per crediti tra privati
  commerciale: 12.0,   // BCE + 8 punti (D.Lgs 231/2002), per B2B
  tributario: 5.0,     // Per debiti verso Fisco/INPS
};

const InteressiMora = () => {
  const [capitale, setCapitale] = useState('5000');
  const [dataScadenza, setDataScadenza] = useState(() => {
    const d = new Date(); d.setMonth(d.getMonth() - 3);
    return d.toISOString().split('T')[0];
  });
  const [dataPagamento, setDataPagamento] = useState(() => new Date().toISOString().split('T')[0]);
  const [tipoMora, setTipoMora] = useState('legale');

  const C = parseFloat(capitale) || 0;
  const tasso = TASSI[tipoMora] || TASSI.legale;

  const { giorni, interessi, totale } = useMemo(() => {
    const d1 = new Date(dataScadenza);
    const d2 = new Date(dataPagamento);
    const diffMs = d2 - d1;
    const giorni = Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
    const interessi = C * (tasso / 100) * (giorni / 365);
    return { giorni, interessi, totale: C + interessi };
  }, [C, tasso, dataScadenza, dataPagamento]);

  const moraColor = tipoMora === 'legale' ? '#3D7A52' : tipoMora === 'commerciale' ? '#C9A84C' : '#5B8DB8';

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="interessi-mora-calc">
      <EducBox text="Gli interessi di mora scattano automaticamente alla scadenza del pagamento, senza necessità di messa in mora formale (per i rapporti commerciali tra imprese, D.Lgs. 231/2002). Per i privati si applica il tasso legale, per le transazioni B2B il tasso commerciale (BCE + 8 punti)." />

      <div className="grid sm:grid-cols-2 gap-4 mb-6">
        <div>
          <label className="text-[#888780] text-sm block mb-1">Importo del debito (€)</label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#888780] text-sm">€</span>
            <input type="number" value={capitale} onChange={e => setCapitale(e.target.value)}
              className="w-full bg-[#1C1C1A] border border-white/[0.08] text-[#E8E8E4] rounded-md pl-7 pr-3 py-2 text-sm" />
          </div>
        </div>
        <div>
          <label className="text-[#888780] text-sm block mb-1">Tipo di mora</label>
          <select value={tipoMora} onChange={e => setTipoMora(e.target.value)}
            className="w-full bg-[#1C1C1A] border border-white/[0.08] text-[#E8E8E4] rounded-md px-3 py-2 text-sm">
            <option value="legale">Legale — privati/consumatori ({TASSI.legale}%)</option>
            <option value="commerciale">Commerciale B2B — D.Lgs.231/2002 ({TASSI.commerciale}%)</option>
            <option value="tributario">Tributario — Fisco/INPS ({TASSI.tributario}%)</option>
          </select>
        </div>
        <div>
          <label className="text-[#888780] text-sm block mb-1">Data scadenza pagamento</label>
          <input type="date" value={dataScadenza} onChange={e => setDataScadenza(e.target.value)}
            className="w-full bg-[#1C1C1A] border border-white/[0.08] text-[#E8E8E4] rounded-md px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="text-[#888780] text-sm block mb-1">Data pagamento effettivo</label>
          <input type="date" value={dataPagamento} onChange={e => setDataPagamento(e.target.value)}
            className="w-full bg-[#1C1C1A] border border-white/[0.08] text-[#E8E8E4] rounded-md px-3 py-2 text-sm" />
        </div>
      </div>

      {/* Tasso evidenziato */}
      <div className="mb-6 p-4 rounded-xl text-center" style={{ background: `rgba(201,168,76,0.06)`, border: `1px solid ${moraColor}40` }}>
        <p className="text-xs text-[#888780] mb-1">Tasso mora applicato ({tipoMora})</p>
        <p className="text-4xl font-bold" style={{ color: moraColor }}>{tasso.toFixed(2)}%</p>
        <p className="text-xs text-[#888780] mt-1">annuo — aggiornato 1° semestre 2026</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <ResultCard label="Giorni di ritardo" value={`${giorni} gg`} />
        <ResultCard label="Interessi di mora" value={`€${fmt(interessi)}`} gold />
        <ResultCard label="Capitale originale" value={`€${fmt(C)}`} />
        <ResultCard label="Totale da pagare" value={`€${fmt(totale)}`} green />
      </div>

      {/* Formula visibile */}
      <div className="p-4 rounded-xl text-sm" style={{ background: '#252523', border: '1px solid rgba(255,255,255,0.06)' }}>
        <p className="text-xs text-[#888780] mb-2 font-medium">Formula applicata</p>
        <p className="text-[#E8E8E4] font-mono text-xs">
          Interessi = €{fmt(C)} × {tasso}% × ({giorni} / 365) = <strong className="text-[#C9A84C]">€{fmt(interessi)}</strong>
        </p>
        <p className="text-xs text-[#888780] mt-2">
          {tipoMora === 'commerciale'
            ? 'Base: D.Lgs. 231/2002 — tasso BCE + 8 punti percentuali. Scatta automaticamente senza messa in mora.'
            : tipoMora === 'tributario'
            ? 'Per debiti fiscali (IRPEF, IVA, contributi INPS) — tasso determinato ogni anno dal MEF.'
            : 'Tasso legale ex art. 1284 c.c. — applicabile a crediti tra privati e consumatori.'}
        </p>
      </div>
</motion.div>
  );
};

export default InteressiMora;
